import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';
import { MaintenanceApp } from '../../common/components/MaintenanceApp';
import { UnhealthyApp } from '../../common/components/UnhealthyApp';
import { CommonContextsProviders } from '../../common/contexts/CommonContextsProviders';
import { getHostAndDomain } from '../../common/global/helpers';
import { useInactivity } from '../../common/hooks/useInactivity';
import useMount from '../../common/hooks/useMount';
import Button from '../../common/widgets/Button';
import Dialog from '../../common/widgets/Dialog';
import Icons from '../../common/widgets/Icons';
import Loader from '../../common/widgets/Loader';
import LoadingSpinner from '../../common/widgets/LoadingSpinner';
import { Routing } from './Routing';
import { config } from './config';
import { AccountInfoProvider } from './contexts/AccountInfoContext';
import './resources/i18n';
import { resources } from './resources/resources';
import { systemService } from './services/system';

export function MerchantsApp() {
  const [isHealthyLoading, setIsHealthyLoading] = useState(true);
  const [isUnderMaintenanceLoading, setIsUnderMaintenanceLoading] =
    useState(true);
  const [isHealthy, setIsHealthy] = useState(true);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  const checkSystemHealth = async (abortController?: AbortController) => {
    const healthResponse = await systemService.getSystemHealth({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) {
      return;
    }
    setIsHealthyLoading(false);

    if (healthResponse.hasErrors) {
      setIsHealthy(false);
      return;
    }

    setIsHealthy(true);

    const maintenanceResponse = await systemService.getSystemReadiness({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) {
      return;
    }
    setIsUnderMaintenanceLoading(false);

    if (maintenanceResponse.hasErrors) {
      setIsUnderMaintenance(true);
    } else {
      setIsUnderMaintenance(false);
    }
  };

  useMount(checkSystemHealth);

  useMount(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('impersonate')) {
      const storageKey = `oidc.user:${config.idpAuthority}:${config.idpClientId}`;
      const oidcStorage = localStorage.getItem(storageKey);
      if (oidcStorage) {
        localStorage.removeItem(storageKey);
      }
    }
  });

  return (
    <>
      <Helmet>
        <meta
          httpEquiv="Content-Security-Policy"
          content={`
                      default-src 'self';
                      connect-src 'self' ${getHostAndDomain(
                        config.merchantsApiUrl,
                      )} ${getHostAndDomain(
                        config.idpUrl,
                      )} https://druidapi.druidplatform.com https://directline.botframework.com;
                      script-src 'self' https://prod-druid-apc.azureedge.net;
                      object-src 'none';
                      style-src 'unsafe-inline';
                      font-src 'self' data: https://fonts.gstatic.com;
                      img-src 'self' data: ${getHostAndDomain(
                        config.imagesUrl,
                      )} https://www.edenred.ro;
                      media-src 'self' data: ${getHostAndDomain(
                        config.videosUrl,
                      )};
                      frame-src 'self' https://privacyportal-de.onetrust.com;
                `}
        ></meta>
      </Helmet>
      <Loader
        loading={isUnderMaintenanceLoading || isHealthyLoading}
        loader={<LoadingSpinner size="large" />}
      >
        {!isHealthy && !isUnderMaintenance && <UnhealthyApp />}
        {isUnderMaintenance && <MaintenanceApp />}
        {isHealthy && !isUnderMaintenance && <AuthenticatedApp />}
      </Loader>
    </>
  );
}

enum GlobalDialogs {
  AuthenticationExpiring = 1,
  AuthenticationExpired = 2,
}

function AuthenticatedApp() {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [visibleDialogId, setVisibleDialogId] = useState<GlobalDialogs | null>(
    null,
  );
  const getInactivitySeconds = useInactivity();

  // always try to silently login once via auth code grant with PKCE or refresh token grant
  useEffect(() => {
    if (
      !hasTriedSignin &&
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !auth.error
    ) {
      window.sessionStorage.setItem(
        'redirectTo',
        `${location.pathname}${location.search}`,
      );

      setHasTriedSignin(true);
      auth.signinRedirect();
    }
  }, [auth, hasTriedSignin]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const inactivitySeconds = getInactivitySeconds();

      if (
        inactivitySeconds > config.idpInactivityWarningSeconds &&
        visibleDialogId === null
      ) {
        setVisibleDialogId(GlobalDialogs.AuthenticationExpiring);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [visibleDialogId, getInactivitySeconds]);

  useEffect(() => {
    const handleAccessTokenExpiring = async () => {
      const user = await auth.signinSilent();
      if (!user) {
        setVisibleDialogId(GlobalDialogs.AuthenticationExpired);
      }
    };

    const handleAccessTokenExpired = () => {
      setVisibleDialogId(GlobalDialogs.AuthenticationExpired);
    };

    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [auth]);

  return (
    <Loader
      loading={!auth.isAuthenticated}
      loader={<LoadingSpinner size="large" />}
    >
      <AppComponent />
      {visibleDialogId === GlobalDialogs.AuthenticationExpiring && (
        <AuthenticationExpiringDialog
          closeDialog={() => setVisibleDialogId(null)}
          showExpiredDialog={() =>
            setVisibleDialogId(GlobalDialogs.AuthenticationExpired)
          }
        />
      )}
      {visibleDialogId === GlobalDialogs.AuthenticationExpired && (
        <AuthenticationExpiredDialog />
      )}
    </Loader>
  );
}

const AppComponent = () => {
  const { t: translate } = useTranslation();

  const currentLanguage =
    localStorage.getItem('language') ?? config.defaultLanguage;

  const loadKendoCustomMessages = () => {
    loadMessages(
      {
        upload: {
          select: translate(resources.upload.button.text),
          dropFilesHere: translate(resources.upload.dropZone.hint),
          headerStatusUploading: translate(
            resources.upload.status.header.uploading,
          ),
          headerStatusUploaded: translate(
            resources.upload.status.header.uploaded,
          ),
          statusUploaded: translate(resources.upload.status.success),
          statusUploadFailed: translate(resources.upload.status.failed),
        },
        calendar: {
          today: translate(resources.calendar.today),
        },
        timepicker: {
          cancel: translate(resources.timepicker.cancel),
          set: translate(resources.timepicker.set),
          now: translate(resources.timepicker.now),
        },
        grid: {
          noRecords: translate(resources.grid.noRecords),
        },
      },
      currentLanguage ?? 'ro',
    );
  };

  loadKendoCustomMessages();

  return (
    <CommonContextsProviders>
      <AccountInfoProvider>
        <LocalizationProvider language={currentLanguage ?? 'ro'}>
          <BrowserRouter>
            <Routing />
          </BrowserRouter>
        </LocalizationProvider>
      </AccountInfoProvider>
    </CommonContextsProviders>
  );
};

const AuthenticationExpiringDialog = (props: {
  closeDialog: () => void;
  showExpiredDialog: () => void;
}) => {
  const { closeDialog, showExpiredDialog } = props;
  const auth = useAuth();
  const { t: translate } = useTranslation();

  useEffect(() => {
    const id: any = setTimeout(() => {
      showExpiredDialog();
    }, 60 * 1000);

    return () => {
      clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onContinueClick = () => {
    closeDialog();
  };

  return (
    <Dialog className="dialog-s">
      <div className="dialog-content">
        <div className="dialog-image">
          <Icons.WarningRed />
        </div>
        <div className="dialog-title">
          {translate(resources.authenticationWarningDialogs.expiring.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.authenticationWarningDialogs.expiring.body)}
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button onClick={onContinueClick} className="btn btn-solid">
          <span className="btn-text">
            {translate(
              resources.authenticationWarningDialogs.expiring.continue,
            )}
          </span>
        </Button>
        <Button
          onClick={() => auth.signoutRedirect()}
          className="btn btn-outline"
        >
          <span className="btn-text">{translate(resources.logout)}</span>
        </Button>
      </div>
    </Dialog>
  );
};

const AuthenticationExpiredDialog = () => {
  const auth = useAuth();
  const { t: translate } = useTranslation();

  useEffect(() => {
    setTimeout(() => {
      if (auth.isAuthenticated) {
        auth.signoutSilent();
      }
      window.location.href = '/';
    }, 7 * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog className="dialog-s">
      <div className="dialog-content">
        <div className="dialog-image">
          <Icons.WarningRed />
        </div>
        <div className="dialog-title">
          {translate(resources.authenticationWarningDialogs.expired.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.authenticationWarningDialogs.expired.body)}
        </div>
      </div>
      <div className="dialog-actions vertical">
        <LoadingSpinner size="medium" />
      </div>
    </Dialog>
  );
};
